import React, {FC} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {Redirect} from 'react-router-dom'

import {Box, Skeleton, Theme, Typography, useTheme} from '@mui/material'
import Dinero from 'dinero.js'
import HTMLReactParser from 'html-react-parser'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as ReceitCheckIcon} from '@progyny/assets/images/receipt-check.svg'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import useLocalizedDate from '@progyny/hooks/useLocalizedDate'
import {SubpageOneColGrid} from '@progyny/pages/Profile/fragments/grid'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {hideZeroCents} from '@progyny/utils/dinero'

const useStyles = makeStyles()((theme: Theme) => ({
  titleBox: {
    backgroundColor: '#FAFAFA',
    display: 'flex',
    paddingLeft: 40,
    borderRadius: 24,
    paddingTop: 0,
    height: 175,
    marginTop: '80px',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',

    [theme.breakpoints.down('sm')]: {
      paddingLeft: 16,
      paddingBottom: 16,
      height: 'auto',
      marginTop: '32px',
    },

    '&::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      height: 60,
      width: '100%',
      backgroundColor: theme.palette.other.brandColors.primary,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
    },
  },
}))

const MemberStatus: FC = () => {
  const user = useMeStore((s) => s.user)
  const loading = useMeStore((s) => s.loading)
  const {t} = useTranslation()
  const {classes} = useStyles()
  const eligibilityStartDate = useLocalizedDate(user?.member.eligibilityStart)
  const {getPath, goTo} = useRoutes()
  const theme = useTheme()

  if (loading) {
    return (
      <Box mt={6} maxWidth={424}>
        <Skeleton variant='rectangular' width={100} height={28} />
        <Typography variant={'h4'}>
          <Skeleton variant={'text'} width={'100%'} />
        </Typography>
        <Box mt={1} className={classes.titleBox}>
          <Box>
            <Skeleton variant='rectangular' height={113} width={93} />
          </Box>
          <Box mx={2}>
            <Typography variant={'h6'}>
              <Skeleton width={'calc(2em + 13ex)'} />
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }
  if (!user) {
    return <Redirect to={getPath(RouteEnum.LOGIN)} />
  }
  const currentBudget: string = hideZeroCents(
    Dinero(user.member.currentBudget as Dinero.Options)
  )
  const employmentPeriod = user.member.contract.aspiringPeriodDescription

  return (
    <Box mt={{xs: 10, md: 15}} mb={theme.spacing(12)}>
      <SubpageOneColGrid title={t('profile_member_status')} lgSize={6}>
        {user.member.isAspiring ? (
          <React.Fragment>
            <Box className={classes.titleBox}>
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                gap={1}
                pt={8}
              >
                <ReceitCheckIcon />
                <Typography variant={'h6'}>
                  {t('profile_member_status_aspiring_member')}
                </Typography>
              </Box>
            </Box>
            <Box mt={6}>
              <Box mt={3}>
                <Typography variant={'body2'} mb={theme.spacing(4)}>
                  {HTMLReactParser(t('profile_member_status_aspiring_intro'))}
                </Typography>
                <Typography variant={'body2'} mb={theme.spacing(4)}>
                  <Trans i18nKey='profile_member_status_aspiring_portal' />
                  <Trans i18nKey='profile_member_status_aspiring_consultation' />
                  <Trans i18nKey='profile_member_status_aspiring_find_clinic' />
                </Typography>
              </Box>
              <Box mt={1}>
                <Typography variant={'body2'} mb={theme.spacing(4)}>
                  {HTMLReactParser(
                    t('profile_member_status_aspiring_after_employment', {
                      employmentPeriod: employmentPeriod,
                      currentBudget: currentBudget,
                      eligibilityStartDate: eligibilityStartDate,
                    })
                  )}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Box mt={1} className={classes.titleBox}>
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                gap={1}
                pt={8}
              >
                <ReceitCheckIcon />
                <Typography variant={'h6'}>
                  {t('profile_member_status_eligible_member')}
                </Typography>
              </Box>
            </Box>
            <Box mt={6}>
              <Box mt={1}>
                <Typography variant={'body2'}>
                  {HTMLReactParser(
                    t('profile_member_status_eligible_intro', {
                      currentBudget,
                    })
                  )}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        )}
        <Box mt={theme.spacing(6)}>
          <Typography variant={'h6'} mb={theme.spacing(2)}>
            {t('profile_member_status_talk_to_progyny')}
          </Typography>
        </Box>
        <Box>
          <Typography variant={'body2'}>
            <Trans i18nKey='profile_member_status_standing_by' />
          </Typography>
        </Box>
        <Box mt={theme.spacing(6)}>
          <SubmitButton
            size={'large'}
            onClick={() => goTo(RouteEnum.CONSULTATIONS)}
          >
            {t('profile_member_status_book_consultation')}
          </SubmitButton>
        </Box>
      </SubpageOneColGrid>
    </Box>
  )
}

export default MemberStatus
